import { type KeyboardEvent, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { Badge } from '@/components/v2/ui'
import type { ReadinessActionState } from '@/types/products'

interface ReadinessActionBadgeProps {
  state: ReadinessActionState
  onClick?: (e: MouseEvent) => void
}

type StateKey = 'review_pending' | 'action_required' | 'in_progress' | 'ready'

const STATE_CONFIG: Record<StateKey, { variant: 'warning' | 'error' | 'info' | 'success'; Icon: typeof Clock; i18nKey: string }> = {
  review_pending:  { variant: 'warning', Icon: Clock,         i18nKey: 'products.readiness_review_pending' },
  action_required: { variant: 'error',   Icon: AlertTriangle, i18nKey: 'products.readiness_action_required' },
  in_progress:     { variant: 'info',    Icon: Loader2,       i18nKey: 'products.readiness_in_progress' },
  ready:           { variant: 'success', Icon: CheckCircle2,  i18nKey: 'products.readiness_ready' },
} as const

export function ReadinessActionBadge({ state, onClick }: ReadinessActionBadgeProps) {
  const { t } = useTranslation()
  const { variant, Icon, i18nKey } = STATE_CONFIG[state]
  const label = t(i18nKey)
  const handleClick = onClick
    ? (e: MouseEvent) => { e.stopPropagation(); onClick(e) }
    : undefined
  const handleKeyDown = onClick
    ? (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClick(e as unknown as MouseEvent) } }
    : undefined

  return (
    <Badge
      className={`readiness-action-badge ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      variant={variant}
      aria-label={label}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Icon className={`w-3 h-3 mr-1${state === 'in_progress' ? ' animate-spin' : ''}`} />
      {label}
    </Badge>
  )
}
